/**
 * Super Admin — OTP Tenants Management (Phase 1)
 * Route: /otp/admin/tenants (super_admin only)
 * Extends the tenant wallet workflow with dedicated OTP columns and admin actions.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Search, RefreshCcw, Filter, Download, Coins, Zap, ShieldAlert, ShieldCheck,
  PauseCircle, PlayCircle, Wallet as WalletIcon, Package, TrendingUp,
  BarChart3, FileClock, Eye, PlusCircle, MinusCircle,
} from "lucide-react";

function money(paise) { return "₹" + (Number(paise || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 }); }
function statusBadge(status) {
  const cls = {
    active: "border-emerald-300 text-emerald-700 dark:text-emerald-400",
    suspended: "border-red-300 text-red-700 dark:text-red-400",
    trial: "border-sky-300 text-sky-700 dark:text-sky-400",
    grace_period: "border-amber-300 text-amber-700 dark:text-amber-400",
    none: "border-zinc-300 text-zinc-500",
  }[status] || "border-zinc-300 text-zinc-500";
  return <Badge variant="outline" className={`rounded-sm text-[10px] uppercase ${cls}`}>{status || "none"}</Badge>;
}

export default function OtpAdminTenants() {
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // company row for drawer
  const [ledger, setLedger] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ plan_id: "", reason: "", grant_free: false, override_credits: "" });
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ delta: 100, reason: "" });
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletForm, setWalletForm] = useState({ delta_paise: 10000, reason: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [t, p, a] = await Promise.all([
        api.get("/admin/otp/tenants", { params: { q, status, plan_id: planFilter } }),
        api.get("/otp/plans"),
        api.get("/admin/otp/analytics"),
      ]);
      setRows(t.data.items || []);
      setPlans(p.data || []);
      setAnalytics(a.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tenants");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, status, planFilter]);

  const openDetail = async (row) => {
    setDetail(row);
    try {
      const { data } = await api.get(`/admin/otp/tenants/${row.company_id}/ledger`, { params: { limit: 30 } });
      setLedger(data.items || []);
    } catch { setLedger([]); }
  };

  const exportCsv = async () => {
    try {
      const url = process.env.REACT_APP_BACKEND_URL + "/api/admin/otp/tenants.csv";
      const r = await fetch(url, { credentials: "include" });
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "otp-tenants.csv"; a.click();
    } catch (e) { toast.error("Export failed"); }
  };

  const assignPlan = async () => {
    if (!assignForm.plan_id) return toast.error("Select a plan");
    if (!assignForm.reason) return toast.error("Reason is required");
    try {
      await api.post(`/admin/otp/tenants/${detail.company_id}/assign-plan`, {
        plan_id: assignForm.plan_id,
        reason: assignForm.reason,
        grant_free: assignForm.grant_free,
        override_credits: assignForm.override_credits ? Number(assignForm.override_credits) : null,
      });
      toast.success("Plan assigned");
      setAssignOpen(false);
      await load();
      await openDetail(detail);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const adjustCredits = async () => {
    const delta = Number(adjustForm.delta);
    if (!Number.isInteger(delta) || delta === 0) return toast.error("Enter a non-zero integer delta");
    if (!adjustForm.reason || adjustForm.reason.length < 3) return toast.error("Provide a reason (min 3 chars)");
    try {
      await api.post(`/admin/otp/tenants/${detail.company_id}/adjust-credits`,
        { delta, reason: adjustForm.reason });
      toast.success("Credits adjusted");
      setAdjustOpen(false);
      await load(); await openDetail(detail);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doSuspend = async () => {
    if (!suspendReason || suspendReason.length < 3) return toast.error("Reason is required");
    try {
      await api.post(`/admin/otp/tenants/${detail.company_id}/suspend`, { reason: suspendReason });
      toast.success("Suspended");
      setSuspendOpen(false); await load(); await openDetail(detail);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doActivate = async () => {
    try {
      await api.post(`/admin/otp/tenants/${detail.company_id}/activate`);
      toast.success("Activated"); await load(); await openDetail(detail);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const adjustWallet = async () => {
    const delta = Number(walletForm.delta_paise);
    if (!Number.isInteger(delta) || delta === 0) return toast.error("Enter a non-zero integer delta (paise)");
    if (!walletForm.reason || walletForm.reason.length < 3) return toast.error("Provide a reason");
    try {
      await api.post(`/admin/otp/tenants/${detail.company_id}/wallet-adjust`,
        { delta_paise: delta, reason: walletForm.reason });
      toast.success("Wallet updated"); setWalletOpen(false);
      await load(); await openDetail(detail);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const kpi = analytics?.kpis || {};

  return (
    <div className="space-y-4" data-testid="otp-admin-tenants">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Super Admin / OTP</div>
          <h1 className="text-3xl font-black tracking-tighter">Tenant Wallets · OTP</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Manage OTP subscriptions, credits, wallet balances and lifecycle for every tenant.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={load} data-testid="refresh-btn">
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={exportCsv} data-testid="export-csv-btn">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { k: "total_tenants", label: "Tenants", icon: Package },
          { k: "active_subscriptions", label: "Active subs", icon: ShieldCheck },
          { k: "suspended_subscriptions", label: "Suspended", icon: PauseCircle },
          { k: "otps_today", label: "OTPs today", icon: Zap },
          { k: "otps_month", label: "OTPs this month", icon: TrendingUp },
          { k: "revenue_month_paise", label: "Revenue MTD", icon: WalletIcon, money: true },
        ].map(({ k, label, icon: Icon, money: m }) => (
          <Card key={k} className="rounded-sm shadow-none">
            <CardContent className="p-3">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                <Icon className="h-3 w-3" />{label}
              </div>
              <div className="text-2xl font-black tracking-tight mt-1" data-testid={`kpi-${k}`}>
                {m ? money(kpi[k] || 0) : (kpi[k] ?? 0)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="rounded-sm shadow-none">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search tenant name or email…"
              className="pl-7 rounded-sm h-9" data-testid="search-input" />
          </div>
          <select value={status} onChange={e => setStatus(e.target.value)}
            className="h-9 px-2 rounded-sm border border-border bg-background text-sm" data-testid="filter-status">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="grace_period">Grace period</option>
            <option value="none">No subscription</option>
          </select>
          <select value={planFilter} onChange={e => setPlanFilter(e.target.value)}
            className="h-9 px-2 rounded-sm border border-border bg-background text-sm" data-testid="filter-plan">
            <option value="">All plans</option>
            {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-sm shadow-none">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2">Tenant</th>
                <th className="px-3 py-2">Plan</th>
                <th className="px-3 py-2">OTP Credits</th>
                <th className="px-3 py-2">Month usage</th>
                <th className="px-3 py-2">General Wallet</th>
                <th className="px-3 py-2">Keys</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Last OTP</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const used = r.otp_credits_used || 0;
                const remaining = r.otp_credits_remaining || 0;
                const total = r.otp_credits_total || 0;
                const pct = total ? Math.min(100, Math.round((used / total) * 100)) : 0;
                return (
                  <tr key={r.company_id} className="border-t border-border hover:bg-muted/30" data-testid={`tenant-row-${r.company_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{r.email || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{r.otp_plan?.name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2">
                      <div className="text-xs font-mono">{used.toLocaleString()} / {total.toLocaleString()}</div>
                      <div className="w-24 h-1 bg-muted rounded-sm mt-1 overflow-hidden">
                        <div className="h-full bg-orange-500" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-[10px] text-emerald-700">{remaining.toLocaleString()} left</div>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">{r.month_usage}</td>
                    <td className="px-3 py-2 text-xs font-mono">{money(r.wallet_balance_paise)}</td>
                    <td className="px-3 py-2 text-xs font-mono">{r.api_keys_active} / {r.api_keys_total}</td>
                    <td className="px-3 py-2">{statusBadge(r.otp_status)}</td>
                    <td className="px-3 py-2 text-[10px] font-mono text-muted-foreground">{r.last_otp_at ? new Date(r.last_otp_at).toLocaleString() : "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" className="rounded-sm gap-1"
                        onClick={() => openDetail(r)} data-testid={`view-tenant-${r.company_id}`}>
                        <Eye className="h-3 w-3" /> View
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="text-center text-muted-foreground py-10">{loading ? "Loading…" : "No tenants match."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail Sheet */}
      <Sheet open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="tenant-detail-sheet">
          <SheetHeader>
            <SheetTitle className="text-2xl font-black tracking-tight">{detail?.name}</SheetTitle>
          </SheetHeader>
          {detail && (
            <div className="mt-4 space-y-4 pb-8">
              <div className="grid grid-cols-2 gap-3">
                <Card className="rounded-sm shadow-none">
                  <CardContent className="p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Plan</div>
                    <div className="text-sm font-semibold">{detail.otp_plan?.name || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">{statusBadge(detail.otp_status)}</div>
                  </CardContent>
                </Card>
                <Card className="rounded-sm shadow-none">
                  <CardContent className="p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">OTP Credits</div>
                    <div className="text-2xl font-black">{detail.otp_credits_remaining} <span className="text-xs text-muted-foreground">/ {detail.otp_credits_total}</span></div>
                    <div className="text-[10px] text-muted-foreground">Used: {detail.otp_credits_used}</div>
                  </CardContent>
                </Card>
                <Card className="rounded-sm shadow-none">
                  <CardContent className="p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">General Wallet</div>
                    <div className="text-2xl font-black">{money(detail.wallet_balance_paise)}</div>
                  </CardContent>
                </Card>
                <Card className="rounded-sm shadow-none">
                  <CardContent className="p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">API keys / Month usage</div>
                    <div className="text-sm">{detail.api_keys_active} active · {detail.month_usage} OTPs</div>
                    <div className="text-[10px] text-muted-foreground">Last: {detail.last_otp_at ? new Date(detail.last_otp_at).toLocaleString() : "—"}</div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="rounded-sm gap-1" onClick={() => { setAssignForm({ plan_id: detail.otp_plan?.id || "", reason: "", grant_free: false, override_credits: "" }); setAssignOpen(true); }} data-testid="assign-plan-btn">
                  <Package className="h-3 w-3" /> Assign / Change Plan
                </Button>
                <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => { setAdjustForm({ delta: 100, reason: "" }); setAdjustOpen(true); }} data-testid="adjust-credits-btn">
                  <Coins className="h-3 w-3" /> Adjust OTP Credits
                </Button>
                <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => { setWalletForm({ delta_paise: 10000, reason: "" }); setWalletOpen(true); }} data-testid="adjust-wallet-btn">
                  <WalletIcon className="h-3 w-3" /> Adjust General Wallet
                </Button>
                {detail.otp_status === "suspended" ? (
                  <Button size="sm" variant="outline" className="rounded-sm gap-1 border-emerald-300 text-emerald-700" onClick={doActivate} data-testid="activate-btn">
                    <PlayCircle className="h-3 w-3" /> Reactivate OTP
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="rounded-sm gap-1 border-red-300 text-red-700" onClick={() => setSuspendOpen(true)} data-testid="suspend-btn">
                    <PauseCircle className="h-3 w-3" /> Suspend OTP
                  </Button>
                )}
              </div>

              {/* Ledger */}
              <Card className="rounded-sm shadow-none">
                <CardContent className="p-3">
                  <div className="flex items-center gap-1 text-sm font-semibold mb-2">
                    <FileClock className="h-4 w-4" /> OTP Transaction Ledger
                  </div>
                  <div className="max-h-72 overflow-y-auto text-xs">
                    <table className="w-full">
                      <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                        <tr>
                          <th className="px-2 py-1 text-left">When</th>
                          <th className="px-2 py-1 text-left">Type</th>
                          <th className="px-2 py-1 text-right">Δ Credits</th>
                          <th className="px-2 py-1 text-right">Δ Wallet</th>
                          <th className="px-2 py-1 text-left">Source / Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledger.map(l => (
                          <tr key={l.id} className="border-t border-border" data-testid={`ledger-${l.id}`}>
                            <td className="px-2 py-1 font-mono text-[10px]">{new Date(l.created_at).toLocaleString()}</td>
                            <td className="px-2 py-1">{l.transaction_type}</td>
                            <td className={`px-2 py-1 text-right font-mono ${l.included_credit_delta > 0 ? "text-emerald-700" : l.included_credit_delta < 0 ? "text-red-700" : ""}`}>
                              {l.included_credit_delta > 0 ? "+" : ""}{l.included_credit_delta || 0}
                            </td>
                            <td className={`px-2 py-1 text-right font-mono ${l.wallet_delta_paise > 0 ? "text-emerald-700" : l.wallet_delta_paise < 0 ? "text-red-700" : ""}`}>
                              {l.wallet_delta_paise ? money(l.wallet_delta_paise) : "—"}
                            </td>
                            <td className="px-2 py-1 text-[10px] text-muted-foreground">{l.source} · {(l.meta?.reason || l.meta?.plan_name || "").slice(0, 60)}</td>
                          </tr>
                        ))}
                        {ledger.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-4">No transactions</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Assign Plan Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>Assign / Change OTP Plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Plan</Label>
              <select value={assignForm.plan_id} onChange={e => setAssignForm({ ...assignForm, plan_id: e.target.value })}
                className="w-full h-9 px-2 rounded-sm border border-border bg-background text-sm" data-testid="assign-plan-select">
                <option value="">— select plan —</option>
                {plans.map(p => <option key={p.id} value={p.id}>{p.name} — {p.included_otp_count} OTPs · ₹{(p.monthly_price_paise/100).toLocaleString()}/mo</option>)}
              </select>
            </div>
            <div><Label>Override credits (optional)</Label>
              <Input type="number" value={assignForm.override_credits}
                onChange={e => setAssignForm({ ...assignForm, override_credits: e.target.value })}
                placeholder="Leave blank to use plan's included_otp_count"
                className="rounded-sm" data-testid="assign-override-credits" />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={assignForm.grant_free}
                onChange={e => setAssignForm({ ...assignForm, grant_free: e.target.checked })}
                data-testid="assign-grant-free" />
              Grant free (no payment / no wallet debit — for support / trials)
            </label>
            <div><Label>Reason</Label>
              <Textarea value={assignForm.reason}
                onChange={e => setAssignForm({ ...assignForm, reason: e.target.value })}
                placeholder="e.g. Onboarding trial, support escalation, migration"
                className="rounded-sm" rows={2} data-testid="assign-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-sm" onClick={assignPlan} data-testid="assign-plan-submit">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Credits Dialog */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>Adjust OTP Credits</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Delta (positive = grant, negative = revoke)</Label>
              <div className="flex gap-2">
                <Button variant="outline" className="rounded-sm gap-1" size="sm"
                  onClick={() => setAdjustForm({ ...adjustForm, delta: -Math.abs(adjustForm.delta || 0) || -100 })}>
                  <MinusCircle className="h-3 w-3" />
                </Button>
                <Input type="number" value={adjustForm.delta}
                  onChange={e => setAdjustForm({ ...adjustForm, delta: e.target.value })}
                  className="rounded-sm text-center" data-testid="adjust-credits-delta" />
                <Button variant="outline" className="rounded-sm gap-1" size="sm"
                  onClick={() => setAdjustForm({ ...adjustForm, delta: Math.abs(adjustForm.delta || 0) || 100 })}>
                  <PlusCircle className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div><Label>Reason</Label>
              <Textarea value={adjustForm.reason}
                onChange={e => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                placeholder="e.g. Compensating for outage, top-up bonus, downgrade"
                className="rounded-sm" rows={2} data-testid="adjust-credits-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-sm" onClick={adjustCredits} data-testid="adjust-credits-submit">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-red-700"/> Suspend OTP service</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-muted-foreground text-xs">All OTP send/verify calls for this tenant will start returning <code>SUBSCRIPTION_INACTIVE</code> immediately.</div>
            <Textarea value={suspendReason} onChange={e => setSuspendReason(e.target.value)}
              placeholder="Why? (visible in audit log)" className="rounded-sm" rows={2} data-testid="suspend-reason" />
          </div>
          <DialogFooter>
            <Button variant="destructive" className="rounded-sm" onClick={doSuspend} data-testid="suspend-submit">Suspend</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wallet Adjust Dialog */}
      <Dialog open={walletOpen} onOpenChange={setWalletOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>Adjust General Wallet</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-muted-foreground text-xs">Delta in <b>paise</b>. Positive credits the wallet, negative debits. This does NOT change OTP credits.</div>
            <div><Label>Delta (paise)</Label>
              <Input type="number" value={walletForm.delta_paise}
                onChange={e => setWalletForm({ ...walletForm, delta_paise: e.target.value })}
                className="rounded-sm" data-testid="wallet-delta" />
              <div className="text-[10px] text-muted-foreground">= {money(walletForm.delta_paise)}</div>
            </div>
            <div><Label>Reason</Label>
              <Textarea value={walletForm.reason} onChange={e => setWalletForm({ ...walletForm, reason: e.target.value })}
                placeholder="e.g. Manual top-up, refund, adjustment"
                className="rounded-sm" rows={2} data-testid="wallet-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-sm" onClick={adjustWallet} data-testid="wallet-adjust-submit">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
